import React, { useCallback, useMemo, useState } from 'react';
import { CatalogProvider } from '@context/catalog.context';
import clsx from 'clsx';
import useCatalogSchema from '@hooks/catalog-product/use-catalog-schema';
import CatalogSchema from '@components/structured-schemas/catalog-schema';
import CurrentRefinementSection from '@components/catalog/current-refinement-section';
import ProductSection from '@components/catalog/product-section';
import BottomPaginationSection from '@components/catalog/bottom-pagination-section';
import { CampaignProvider, CampaignProviderProps } from '@context/campaign.context';
import FilterSectionWrapper from '@components/catalog/filter-section-wrapper';
import CatalogDatalayer from '@components/catalog/catalog-datalayer/catalog-datalayer';
import ManageRecentSearches from '@components/catalog/manage-recent-searches/manage-recent-searches';
import MobileActionBar from '@components/catalog/product-search/mobile-action-bar/mobile-action-bar';
import SuggestedSearchQuery from '@components/catalog/product-search/suggested-search-query/suggested-search-query';
import { PLPInnerFeedContextProvider } from '@context/plp-inner-feed.context';
import { CatalogProviderProps } from '@interfaces/models/catalogContextProps';
import { handleMobileFilterBodyClass } from '@helpers/utils/catalog/catalog-ui-utils';
import dynamic from 'next/dynamic';
import styles from './product-search.module.scss';

export type ProductSearchProps = {
  catalogContextProps: Omit<CatalogProviderProps, 'children'>;
  campaignContextProps?: Omit<CampaignProviderProps, 'children'>;
  // Campaign/heroPDP header
  headerContent?: React.ReactNode;
  // SEO links, SEO footer
  footerContent?: React.ReactNode;
};

const CatalogHotfiltersVariantContainer = dynamic(
  () => import('@components/catalog/catalog-hotfilters-variant-container'),
);

const ProductSearch: React.FC<ProductSearchProps> = (props): React.JSX.Element => {
  const { catalogContextProps, campaignContextProps, headerContent, footerContent } = props;

  const searchQuery = catalogContextProps.query;
  const isPlpHotfiltersEnabled = catalogContextProps.isPlpHotfiltersEnabled;
  const schemaCatalogPage = useCatalogSchema();

  const [isSortModalOpen, setIsSortModalOpen] = useState<boolean>(false);
  const [areMobileFiltersOpened, setAreMobileFiltersOpenedState] = useState<boolean>(false);
  const setAreMobileFiltersOpened = useCallback((state: boolean) => {
    setAreMobileFiltersOpenedState(state);
    handleMobileFilterBodyClass(state);
  }, []);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const bottomSection = useMemo(
    () => (
      <>
        <BottomPaginationSection />
        {footerContent && <>{footerContent}</>}
      </>
    ),
    [footerContent],
  );

  return (
    <>
      <CampaignProvider {...campaignContextProps}>
        <CatalogProvider {...{ ...catalogContextProps, scrollRef }}>
          <PLPInnerFeedContextProvider>
            <ManageRecentSearches />
            <CatalogDatalayer>
              {schemaCatalogPage?.isSingleSchema && <CatalogSchema />}
              <main className={styles.catalogPage}>
                {!isPlpHotfiltersEnabled && (
                  <>
                    {headerContent && <>{headerContent}</>}
                    {!!searchQuery && (
                      <div className={styles.catalogPage__mobileSearchTitle}>
                        <span className={styles.catalogPage__mobileSearchTitle__text}>{searchQuery}</span>
                        <SuggestedSearchQuery />
                      </div>
                    )}
                  </>
                )}
                {!isPlpHotfiltersEnabled && (
                  <MobileActionBar
                    onSort={() => setIsSortModalOpen(true)}
                    onFilter={() => setAreMobileFiltersOpened(true)}
                  />
                )}
                <div
                  className={clsx(
                    styles.catalogPage__catalog,
                    isPlpHotfiltersEnabled && styles.catalogPage__catalog__hotfilters_variant,
                  )}
                  ref={isPlpHotfiltersEnabled ? undefined : scrollRef}
                >
                  <div className="innerContainer">
                    <div className={clsx(styles.catalog)}>
                      {!isPlpHotfiltersEnabled && (
                        <FilterSectionWrapper
                          areMobileFiltersOpened={areMobileFiltersOpened}
                          setAreMobileFiltersOpened={setAreMobileFiltersOpened}
                        />
                      )}
                      <div className={styles.catalog__columnProductList}>
                        {isPlpHotfiltersEnabled ? (
                          <CatalogHotfiltersVariantContainer
                            metadata={catalogContextProps.metadata}
                            scrollRef={scrollRef}
                          />
                        ) : (
                          <CurrentRefinementSection
                            isSortModalOpen={isSortModalOpen}
                            setIsSortModalOpen={setIsSortModalOpen}
                          />
                        )}
                        <ProductSection />
                      </div>
                    </div>
                    <div className="d-flex">
                      <div className={clsx('vc-d-none vc-d-md-block', styles.bottomLeftBlock)} />
                      <div className={styles.bottomSectionWrapper}>{bottomSection}</div>
                    </div>
                  </div>
                </div>
              </main>
            </CatalogDatalayer>
          </PLPInnerFeedContextProvider>
        </CatalogProvider>
      </CampaignProvider>
    </>
  );
};

export default ProductSearch;
